import {
  collageSpares,
  fillRepeats,
  isEmptyCollage,
  randomCollageSeed,
  shuffleCollage,
  type CollageStatus,
} from '@pz/scene';
import { donePicking } from '../photo-intake.js';
import { PHOTO_TEXTS } from '../photoquality.js';
import type { OptionDef } from '../types.js';
import type { CollageHost } from './host.js';
import type { CollageIntake, CollageJob } from './intake.js';
import { COLLAGE_TEXTS, collageMissingMessage } from './texts.js';

/**
 * The collage question's step (docs/photo-collage.md §7): pick every photo at
 * once, watch them arrive, see where things stand, and go to the board.
 *
 *  A. empty       — "Izaberi slike" (multi-select, photos only, no camera);
 *  B. uploading   — "Šaljem slike… 9 od 24", a bar, the tray filling in;
 *  C. complete    — "✓ Sve slike su na mestu." [Uredi raspored] [Promešaj]
 *                   [Dodaj još slika], spares, faces and quality notes;
 *  D. incomplete  — "Nedostaju još 3 slike." [Dodaj slike] [Ponovi slike].
 *
 * Every change of the answer is a pure `@pz/scene` command committed through
 * the host (undoable); the step only draws what the host and the intake say.
 */

/** Tray thumbnails shown before "Prikaži sve": two rows of five. */
export const COLLAGE_TRAY_ROWS = 10;

const ICON_PHOTOS =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2.5" y="5" width="11" height="11" rx="1.5"/><path d="M6.5 5V3.5A1 1 0 0 1 7.5 2.5h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H13.5"/><path d="M2.5 13l3-3 3 3 2-2 3 3"/></svg>';
const ICON_BOARD =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><rect x="2.5" y="2.5" width="6.5" height="8" rx="1"/><rect x="11" y="2.5" width="6.5" height="5" rx="1"/><rect x="2.5" y="12.5" width="6.5" height="5" rx="1"/><rect x="11" y="9.5" width="6.5" height="8" rx="1"/></svg>';
const ICON_SHUFFLE =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 6h3.2c1.6 0 2.6.7 3.5 2l2.6 4c.9 1.3 1.9 2 3.5 2h2.2"/><path d="M2.5 14h3.2c1.2 0 2-.4 2.7-1.1M11.6 7.1c.7-.7 1.5-1.1 2.7-1.1h2.2"/><path d="M15.5 4l2 2-2 2M15.5 12l2 2-2 2"/></svg>';
const ICON_PLUS =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M10 4v12M4 10h12"/></svg>';
const ICON_REPEAT =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><rect x="2.5" y="2.5" width="10" height="10" rx="1.5"/><rect x="7.5" y="7.5" width="10" height="10" rx="1.5"/></svg>';

export interface CollageStepOptions {
  option: OptionDef;
  host: CollageHost;
  intake: CollageIntake;
  /** Opens the picker: flushes the draft and notes the open picker first (form.openPicker). */
  openPicker(input: HTMLInputElement): void;
  /** A short message with an optional action ("Poništi"). */
  toast(message: string, action?: { label: string; onClick(): void }): void;
  random?(): number;
}

export interface CollageStep {
  element: HTMLElement;
  input: HTMLInputElement;
  /** Draws the current state again. */
  refresh(): void;
  /** Every active slot filled (or empty ones allowed) and nothing on its way. */
  complete(): boolean;
  /** Photos are in, or on their way: the question is under way even if optional. */
  started(): boolean;
  /** Photos are still being sent. */
  sending(): boolean;
  /** Why "Dalje" / add-to-cart waits, when it does: sending, or slots still empty. */
  missingMessage(): string | undefined;
  /** Opens the picker: new photos, or one photo for `target`'s slot. */
  pick(target?: { elementId: string }): void;
  /** Files from the picker (or a drop, or a test). */
  receive(files: File[], target?: string): Promise<void>;
  /** The page was reloaded while the gallery was open. */
  noteInterrupted(): void;
  /** Tells the shopper something about the collage in the step's note line ('' clears it). */
  note(text: string): void;
  /** Says a sentence to a screen reader (the step's live region). */
  announce(text: string): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(className: string, label: string, icon?: string): HTMLButtonElement {
  const node = el('button', className);
  node.type = 'button';
  if (icon) {
    node.innerHTML = icon;
    const text = el('span', 'pz-collage-btn-text', label);
    node.appendChild(text);
  } else {
    node.textContent = label;
  }
  node.setAttribute('aria-label', label);
  return node;
}

function show(node: HTMLElement, visible: boolean): void {
  node.hidden = !visible;
}

/** Android on mobile data: the one place the Wi-Fi hint is worth its line. */
function onMobileData(): boolean {
  try {
    const nav = navigator as Navigator & { connection?: { type?: string; effectiveType?: string } };
    return /Android/i.test(nav.userAgent ?? '') && nav.connection?.type === 'cellular';
  } catch {
    return false;
  }
}

export function createCollageStep(options: CollageStepOptions): CollageStep {
  const { option, host, intake } = options;
  const rules = host.rules;
  const config = option.config ?? {};
  const random = options.random ?? Math.random;

  const root = el('div', 'pz-collage');
  const intro = el('p', 'pz-hint pz-collage-intro');
  const pickLabel = typeof config.buttonText === 'string' && config.buttonText.trim() ? config.buttonText.trim() : COLLAGE_TEXTS.button;
  const pickButton = button(`pz-drop pz-collage-pick${typeof config.buttonClass === 'string' && config.buttonClass ? ` ${config.buttonClass.replace(/[^A-Za-z0-9_ -]/g, '')}` : ''}`, pickLabel, ICON_PHOTOS);
  const tip = el('p', 'pz-hint pz-collage-tip', COLLAGE_TEXTS.tip);
  const wifi = el('p', 'pz-hint pz-collage-wifi', COLLAGE_TEXTS.wifi);

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = true;
  input.hidden = true;
  input.className = 'pz-collage-input';

  const progress = el('div', 'pz-collage-progress');
  progress.setAttribute('role', 'status');
  const progressText = el('p', 'pz-collage-progress-text');
  const bar = el('div', 'pz-collage-bar');
  const barFill = el('span', 'pz-collage-bar-fill');
  bar.appendChild(barFill);
  bar.setAttribute('aria-hidden', 'true');
  const network = el('p', 'pz-hint pz-collage-net');
  progress.append(progressText, bar, network);

  const state = el('div', 'pz-collage-state');
  const headline = el('p', 'pz-collage-headline');
  const openButton = button('pz-btn pz-collage-open', COLLAGE_TEXTS.openBoard, ICON_BOARD);
  const actions = el('div', 'pz-collage-actions');
  const shuffleButton = button('pz-btn-ghost pz-collage-shuffle', COLLAGE_TEXTS.shuffle, ICON_SHUFFLE);
  const addButton = button('pz-btn-ghost pz-collage-add', COLLAGE_TEXTS.addMore, ICON_PLUS);
  const repeatButton = button('pz-btn-ghost pz-collage-repeat', COLLAGE_TEXTS.repeat, ICON_REPEAT);
  actions.append(shuffleButton, addButton, repeatButton);
  state.append(headline, openButton, actions);

  const failBox = el('div', 'pz-collage-fail');
  failBox.setAttribute('role', 'status');
  const failText = el('p', 'pz-collage-fail-text');
  const resendButton = button('pz-btn-ghost small pz-collage-resend', COLLAGE_TEXTS.resend);
  const unusableList = el('ul', 'pz-collage-unusable');
  failBox.append(failText, resendButton, unusableList);

  const notice = el('p', 'pz-hint pz-collage-notice');
  notice.setAttribute('role', 'status');

  const tray = el('div', 'pz-collage-tray');
  // A group of buttons (each thumbnail is one): a list role would take the
  // button role away from them for a screen reader.
  tray.setAttribute('role', 'group');
  tray.setAttribute('aria-label', COLLAGE_TEXTS.trayLabel);
  const more = button('pz-link pz-collage-more', COLLAGE_TEXTS.showLess);

  const sparesLine = el('p', 'pz-hint pz-collage-spares');
  const facesLine = el('p', 'pz-hint pz-collage-faces');
  const qualityLine = el('p', 'pz-hint pz-collage-quality');
  const live = el('span', 'pz-sr pz-collage-live');
  live.setAttribute('aria-live', 'polite');

  root.append(intro, pickButton, tip, wifi, input, progress, failBox, state, notice, tray, more, sparesLine, facesLine, qualityLine, live);

  let expanded = false;
  let noticeText = '';
  let target: string | undefined;
  const trayItems = new Map<string, HTMLElement>();

  // ── Actions ──────────────────────────────────────────────────────────────

  const pick = (to?: { elementId: string }): void => {
    // Nothing more fits: said now, rather than after a trip to the gallery
    // (and on the board, when that is where "Dodaj slike" was tapped).
    if (!to && intake.capacity().left === 0) {
      noticeText = COLLAGE_TEXTS.full;
      host.notice(COLLAGE_TEXTS.full);
      refresh();
      return;
    }
    target = to?.elementId;
    input.multiple = !target;
    options.openPicker(input);
  };

  const receive = async (files: File[], to?: string): Promise<void> => {
    if (files.length === 0) return;
    const outcome = await intake.add(files, to ? { target: to } : {});
    noticeText = outcome.message ?? '';
    // Picked from the board: the board says it (the step is behind it).
    if (outcome.message) host.notice(outcome.message);
    refresh();
  };

  input.addEventListener('change', () => {
    donePicking();
    const files = [...(input.files ?? [])];
    const to = target;
    target = undefined;
    input.multiple = true;
    input.value = '';
    void receive(files, to);
  });
  input.addEventListener('cancel', () => {
    donePicking();
    target = undefined;
    input.multiple = true;
  });

  pickButton.addEventListener('click', () => pick());
  // Photos dragged in from the desktop, the way people expect a photo field to work.
  pickButton.addEventListener('dragover', (event) => {
    event.preventDefault();
    pickButton.classList.add('is-over');
  });
  pickButton.addEventListener('dragleave', () => pickButton.classList.remove('is-over'));
  pickButton.addEventListener('drop', (event) => {
    event.preventDefault();
    pickButton.classList.remove('is-over');
    const files = [...((event as DragEvent).dataTransfer?.files ?? [])].filter((f) => !f.type || /^image\//.test(f.type));
    if (files.length > 0) void receive(files);
  });
  addButton.addEventListener('click', () => pick());
  openButton.addEventListener('click', () => void host.openBoard().then(refresh));
  shuffleButton.addEventListener('click', () => {
    const answer = host.answer();
    if (!answer) return;
    const next = shuffleCollage(answer, randomCollageSeed(random), host.context());
    if (next === answer) return;
    host.commit(next, COLLAGE_TEXTS.shuffled);
    options.toast(COLLAGE_TEXTS.shuffled, {
      label: COLLAGE_TEXTS.undo,
      onClick: () => {
        if (host.undo()) live.textContent = COLLAGE_TEXTS.undone;
      },
    });
  });
  repeatButton.addEventListener('click', () => {
    const answer = host.answer();
    if (!answer) return;
    const next = fillRepeats(answer, answer.seed ?? randomCollageSeed(random), host.context());
    if (next === answer) return;
    host.commit(next);
    noticeText = COLLAGE_TEXTS.repeated;
    refresh();
  });
  resendButton.addEventListener('click', () => {
    intake.retryFailed();
    refresh();
  });
  more.addEventListener('click', () => {
    expanded = !expanded;
    refresh();
  });

  // ── Drawing ──────────────────────────────────────────────────────────────

  const slotOf = (assetId: string): string | undefined => {
    const answer = host.answer();
    return host.activeIds().find((id) => answer?.slots[id]?.assetId === assetId);
  };

  const trayItem = (key: string): HTMLElement => {
    let item = trayItems.get(key);
    if (!item) {
      item = el('button', 'pz-collage-thumb');
      (item as HTMLButtonElement).type = 'button';
      item.dataset.key = key;
      item.addEventListener('click', () => {
        const job = intake.jobs.find((j) => j.key === key);
        if (job) {
          if (job.state === 'failed' && job.retryable) intake.retry(key);
          return;
        }
        void host.openBoard(slotOf(key)).then(refresh);
      });
      trayItems.set(key, item);
    }
    return item;
  };

  const paintPhoto = (item: HTMLElement, assetId: string, spare: boolean): void => {
    const url = host.thumbUrl(assetId);
    let img = item.querySelector?.('img') as HTMLImageElement | null;
    if (url) {
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        // Eager: a tray thumbnail is a small local copy, and a lazy one can blank while the tray updates.
        img.loading = 'eager';
        item.insertBefore?.(img, item.firstChild ?? null) ?? item.appendChild(img);
      }
      if (img.getAttribute?.('src') !== url) img.src = url;
    }
    item.className = `pz-collage-thumb is-done${spare ? ' is-spare' : ''}`;
    item.setAttribute('aria-label', spare ? COLLAGE_TEXTS.traySpare : COLLAGE_TEXTS.trayDone);
    item.title = spare ? COLLAGE_TEXTS.traySpare : '';
  };

  const paintJob = (item: HTMLElement, job: CollageJob): void => {
    const failed = job.state === 'failed';
    item.className = `pz-collage-thumb ${failed ? (job.retryable ? 'is-failed' : 'is-unusable') : 'is-sending'}`;
    const label = failed ? (job.retryable ? COLLAGE_TEXTS.trayFailed : COLLAGE_TEXTS.trayUnusable) : COLLAGE_TEXTS.traySending;
    item.setAttribute('aria-label', `${job.name}: ${label}`);
    item.title = failed ? job.message ?? label : job.name;
    (item as HTMLButtonElement).disabled = failed && !job.retryable;
  };

  const drawTray = (spares: Set<string>): number => {
    const answer = host.answer();
    const keys: string[] = [];
    const nodes: HTMLElement[] = [];
    for (const photo of answer?.photos ?? []) {
      const item = trayItem(photo.assetId);
      paintPhoto(item, photo.assetId, spares.has(photo.assetId));
      keys.push(photo.assetId);
      nodes.push(item);
    }
    for (const job of intake.jobs) {
      if (job.state === 'done') continue;
      const item = trayItem(job.key);
      paintJob(item, job);
      keys.push(job.key);
      nodes.push(item);
    }
    for (const key of [...trayItems.keys()]) if (!keys.includes(key)) trayItems.delete(key);
    const shown = expanded ? nodes : nodes.slice(0, COLLAGE_TRAY_ROWS);
    syncChildren(tray, shown);
    return nodes.length;
  };

  /** Failed photos already told to the board, by job key (a photo sent again may fail again). */
  const failuresTold = new Set<string>();
  /** A photo that failed while the board is open is said there, where the shopper is looking. */
  const tellBoard = (): void => {
    const failed = intake.jobs.filter((job) => job.state === 'failed');
    const keys = new Set(failed.map((job) => job.key));
    for (const key of [...failuresTold]) if (!keys.has(key)) failuresTold.delete(key);
    const fresh = failed.filter((job) => !failuresTold.has(job.key));
    if (fresh.length === 0) return;
    for (const job of fresh) failuresTold.add(job.key);
    const retryable = fresh.filter((job) => job.retryable).length;
    const words = [
      ...(retryable > 0 ? [COLLAGE_TEXTS.notSent(retryable)] : []),
      ...fresh.filter((job) => !job.retryable).map((job) => job.message ?? COLLAGE_TEXTS.unusable(job.name)),
    ];
    host.notice(words.join(' '));
  };

  function refresh(): void {
    tellBoard();
    const answer = host.answer();
    const status: CollageStatus = host.status();
    const photos = answer?.photos.length ?? 0;
    const sending = intake.sending;
    const progressNow = intake.progress();
    const started = photos > 0 || intake.jobs.length > 0;
    const spares = new Set(answer ? collageSpares(answer, host.activeIds()) : []);

    // A. Nothing yet.
    intro.textContent = COLLAGE_TEXTS.help(status.active);
    show(intro, !started);
    show(pickButton, !started);
    show(tip, !started);
    show(wifi, !started && onMobileData());

    // B. On its way.
    show(progress, Boolean(progressNow));
    if (progressNow) {
      progressText.textContent = intake.resuming
        ? `${COLLAGE_TEXTS.resuming} ${progressNow.done} / ${progressNow.total}`
        : COLLAGE_TEXTS.sending(progressNow.done, progressNow.total);
      const share = progressNow.bytesTotal > 0 ? progressNow.bytesDone / progressNow.bytesTotal : 0;
      barFill.style.width = `${Math.round(Math.max(0.03, Math.min(1, share)) * 100)}%`;
      const net = intake.offline ? COLLAGE_TEXTS.offline : intake.serverBusy ? COLLAGE_TEXTS.busy : '';
      network.textContent = net;
      show(network, net !== '');
    }

    // Failures, once the queue has drained.
    const retryable = intake.retryable;
    const unusable = intake.unusable;
    show(failBox, !sending && (retryable.length > 0 || unusable.length > 0));
    failText.textContent = retryable.length > 0 ? COLLAGE_TEXTS.notSent(retryable.length) : '';
    show(failText, retryable.length > 0);
    show(resendButton, retryable.length > 0);
    unusableList.replaceChildren(
      ...unusable.map((job) => el('li', 'pz-collage-unusable-item', job.message ?? COLLAGE_TEXTS.unusable(job.name))),
    );
    show(unusableList, unusable.length > 0);

    // C / D. Where things stand. It stays on screen while more photos are sent — the
    // count runs down live and the buttons wait — so the panel does not collapse
    // and jump back with every upload.
    const settled = photos > 0 && status.active > 0;
    show(state, settled);
    state.classList.toggle('is-busy', sending);
    for (const control of [openButton, shuffleButton, addButton, repeatButton]) {
      (control as HTMLButtonElement).disabled = sending;
    }
    if (settled) {
      const done = status.complete && status.missing === 0;
      state.classList.toggle('is-complete', done);
      state.classList.toggle('is-incomplete', !done);
      headline.textContent = done || (rules.allowEmpty && status.filled > 0 && status.missing === 0)
        ? `✓ ${COLLAGE_TEXTS.complete}`
        : status.missing > 0
          ? COLLAGE_TEXTS.missing(status.missing)
          : `✓ ${COLLAGE_TEXTS.complete}`;
      const addLabel = done ? COLLAGE_TEXTS.addMore : COLLAGE_TEXTS.add;
      const addText = addButton.querySelector?.('.pz-collage-btn-text');
      if (addText) addText.textContent = addLabel;
      else addButton.textContent = addLabel;
      addButton.setAttribute('aria-label', addLabel);
      show(shuffleButton, status.filled > 1);
      show(repeatButton, !done && status.missing > 0 && rules.allowRepeats && status.distinct + status.spares > 0);
      openButton.classList.toggle('pz-btn-ghost', !done);
      openButton.classList.toggle('pz-btn', done);
    }

    notice.textContent = noticeText;
    show(notice, noticeText !== '');

    const count = drawTray(spares);
    show(tray, count > 0);
    show(more, count > COLLAGE_TRAY_ROWS);
    more.textContent = expanded ? COLLAGE_TEXTS.showLess : COLLAGE_TEXTS.showAll(count);

    sparesLine.textContent = status.spares > 0 ? COLLAGE_TEXTS.spares(status.spares) : '';
    show(sparesLine, !sending && status.spares > 0);
    show(facesLine, !sending && status.facesCut.length > 0);
    facesLine.textContent = COLLAGE_TEXTS.facesHint;
    const soft = !sending && host.activeIds().some((id) => host.slotAdvice(id).soft);
    qualityLine.textContent = PHOTO_TEXTS.warn;
    show(qualityLine, soft);

    root.classList.toggle('is-started', started);
    root.classList.toggle('is-sending', sending);
  }

  const complete = (): boolean => {
    if (intake.sending) return false;
    const answer = host.answer();
    if (!answer || isEmptyCollage(answer)) return false;
    return host.status().complete;
  };

  const started = (): boolean => {
    const answer = host.answer();
    return Boolean(answer && answer.photos.length > 0) || intake.sending;
  };

  const missingMessage = (): string | undefined => {
    if (intake.sending) return COLLAGE_TEXTS.wait;
    const answer = host.answer();
    // Nothing chosen yet: say how many, not "Popuni ovo polje".
    if (!answer || answer.photos.length === 0) return COLLAGE_TEXTS.addPhotos(rules.allowEmpty ? 0 : host.status().active);
    const status = host.status();
    if (status.missing > 0 && !rules.allowEmpty) {
      return collageMissingMessage(status.missing, rules.allowRepeats && answer.photos.length > 0);
    }
    return undefined;
  };

  refresh();

  return {
    element: root,
    input,
    refresh,
    complete,
    started,
    sending: () => intake.sending,
    missingMessage,
    pick,
    receive,
    noteInterrupted: () => {
      noticeText = COLLAGE_TEXTS.interrupted;
      refresh();
    },
    note: (text: string) => {
      noticeText = text;
      refresh();
    },
    announce: (text: string) => {
      live.textContent = text;
    },
  };
}

/**
 * Puts `wanted` in `parent` in this order, moving only what is out of place. A
 * thumbnail that is already where it belongs is left alone, so its picture and
 * its spinner do not restart on every progress tick of an upload.
 */
export function syncChildren(parent: HTMLElement, wanted: readonly HTMLElement[]): void {
  const keep = new Set<Element>(wanted);
  for (const child of Array.from(parent.children)) if (!keep.has(child)) child.remove();
  wanted.forEach((node, index) => {
    const at = parent.children[index] ?? null;
    if (at !== node) parent.insertBefore(node, at);
  });
}
